import React, { useState, ChangeEvent, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Switch,
  FormControlLabel,
  IconButton,
  Box,
  SelectChangeEvent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import * as yup from "yup";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

export interface Feature {
  feature: string;
  is_included: boolean;
  is_unlimited_usage: boolean;
}

export interface PaymentPlan {
  name: string;
  type: "USER" | "ORGANIZATION";
  credits: number;
  duration_type: "MONTHLY" | "QUARTERLY" | "ANNUAL" | "LIFETIME";
  features: Feature[];
  credit_rollover: boolean;
  price: number;
  description: string;
  is_unlimited: boolean;
  duration: number;
  category: "FREE" | "BASIC" | "PLUS" | "PRO";
}

interface PaymentPlanFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: PaymentPlan) => void;
}

const initialFormState: PaymentPlan = {
  name: "",
  type: "USER",
  credits: 0,
  duration_type: "MONTHLY",
  features: [],
  credit_rollover: false,
  price: 0,
  description: "",
  is_unlimited: false,
  duration: 0,
  category: "FREE",
};

const paymentPlanSchema = yup.object().shape({
  name: yup.string().required("Plan name is required"),
  type: yup
    .string()
    .oneOf(["USER", "ORGANIZATION"], "Invalid plan type")
    .required("Plan type is required"),
  credits: yup
    .number()
    .min(0, "Credits must be at least 0")
    .required("Plan credits are required"),
  duration_type: yup
    .string()
    .oneOf(
      ["MONTHLY", "QUARTERLY", "ANNUAL", "LIFETIME"],
      "Invalid duration type"
    )
    .required("Duration type is required"),
  price: yup
    .number()
    .min(0, "Price must be at least 0")
    .required("Plan price is required"),
  description: yup.string().required("Plan description is required"),
  duration: yup
    .number()
    .min(0, "Duration must be at least 0")
    .when("duration_type", (duration_type: any, schema: yup.NumberSchema) => {
      return duration_type !== "LIFETIME"
        ? schema.required("Duration is required for non-lifetime plans")
        : schema.notRequired();
    }),
  category: yup
    .string()
    .oneOf(["FREE", "BASIC", "PLUS", "PRO"], "Invalid plan category")
    .required("Plan category is required"),
  credit_rollover: yup.boolean(),
  is_unlimited: yup.boolean(),
  features: yup.array().of(
    yup.object().shape({
      feature: yup.string().required("Feature description is required"),
      is_included: yup.boolean(),
      is_unlimited_usage: yup.boolean(),
    })
  ),
});

const CreatePlan: React.FC<PaymentPlanFormProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<PaymentPlan>(initialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const PlanCreateResponse = useSelector(
    (state: any) => state.CreatePaymentPlanData
  );
  
  useEffect(() => {
    if (PlanCreateResponse?.status && PlanCreateResponse?.data) {
      toast.success(PlanCreateResponse?.data?.data?.message);
      setFormData(initialFormState);
      onClose();
    }
  }
  , [PlanCreateResponse]);

  const addFeature = () => {
    setFormData((prev) => ({
      ...prev,
      features: [
        ...prev.features,
        { feature: "", is_included: false, is_unlimited_usage: false },
      ],
    }));
  };

  const removeFeature = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, idx) => idx !== index),
    }));
  };

  const updateFeature = (index: number, key: keyof Feature, value: any) => {
    const updatedFeatures = [...formData.features];
    updatedFeatures[index] = { ...updatedFeatures[index], [key]: value };
    setFormData((prev) => ({ ...prev, features: updatedFeatures }));
  };

  const handleChange = (
    e:
      | ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    if (!name) return;
    const numericFields = ["credits", "price", "duration"];
    setFormData((prev) => ({
      ...prev,
      [name]: numericFields.includes(name)
        ? Number(value)
        : (e.target as HTMLInputElement).type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : value,
    }));
  };

  const handleSubmit = () => {
    paymentPlanSchema
      .validate(formData, { abortEarly: false })
      .then(() => {
        // If validation passes, submit the form
        onSubmit(formData);
        setErrors({});
      })
      .catch((validationError: yup.ValidationError) => {
        // Map errors from Yup to a field-wise error object
        const errorMessages: Record<string, string> = {};
        validationError.inner.forEach((error) => {
          if (error.path) {
            errorMessages[error.path] = error.message;
          }
        });
        setErrors(errorMessages);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Payment Plan</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <label>Plan Name</label>
          <TextField
            name="name"
            value={formData.name}
            onChange={handleChange}
            variant="outlined"
            placeholder="Enter Plan Name"
            fullWidth
            error={Boolean(errors.name)}
            helperText={errors.name}
          />
          <label>Plan Type</label>
          <FormControl
            fullWidth
            variant="outlined"
            error={Boolean(errors.type)}
          >
            <Select name="type" value={formData.type} onChange={handleChange}>
              <MenuItem value="USER">User</MenuItem>
              <MenuItem value="ORGANIZATION">Organization</MenuItem>
            </Select>
            {errors.type && (
              <Box component="span" sx={{ color: "red", fontSize: "0.75rem" }}>
                {errors.type}
              </Box>
            )}
          </FormControl>
          <label>Plan Credits</label>
          <TextField
            name="credits"
            type="number"
            placeholder="Enter Plan Credits"
            onChange={handleChange}
            variant="outlined"
            fullWidth
            error={Boolean(errors.credits)}
            helperText={errors.credits}
            InputProps={{
              inputProps: {
                min: 0,
              },
            }}
          />
          <label>Duration Type</label>
          <FormControl
            fullWidth
            variant="outlined"
            error={Boolean(errors.duration_type)}
          >
            <Select
              name="duration_type"
              value={formData.duration_type}
              onChange={handleChange}
            >
              <MenuItem value="MONTHLY">Monthly</MenuItem>
              <MenuItem value="QUARTERLY">Quarterly</MenuItem>
              <MenuItem value="ANNUAL">Annual</MenuItem>
              <MenuItem value="LIFETIME">Lifetime</MenuItem>
            </Select>
            {errors.duration_type && (
              <Box component="span" sx={{ color: "red", fontSize: "0.75rem" }}>
                {errors.duration_type}
              </Box>
            )}
          </FormControl>
          <label>Plan Price</label>
          <TextField
            name="price"
            type="number"
            placeholder="Enter Plan Price"
            onChange={handleChange}
            variant="outlined"
            fullWidth
            error={Boolean(errors.price)}
            helperText={errors.price}
            InputProps={{
              inputProps: {
                min: 0,
              },
            }}
          />
          <label>Plan Description</label>
          <TextField
            name="description"
            value={formData.description}
            onChange={handleChange}
            variant="outlined"
            placeholder="Enter Plan Description"
            fullWidth
            multiline
            rows={2}
            error={Boolean(errors.description)}
            helperText={errors.description}
          />
          <label>Duration (in Months)</label>
          <TextField
            name="duration"
            type="number"
            placeholder="Enter Duration in Months"
            onChange={handleChange}
            variant="outlined"
            fullWidth
            error={Boolean(errors.duration)}
            helperText={errors.duration}
            InputProps={{
              inputProps: {
                min: 0,
              },
            }}
          />
          <label>Plan Category</label>
          <FormControl
            fullWidth
            variant="outlined"
            error={Boolean(errors.category)}
          >
            <Select
              name="category"
              value={formData.category}
              onChange={handleChange}
            >
              <MenuItem value="FREE">Free</MenuItem>
              <MenuItem value="BASIC">Basic</MenuItem>
              <MenuItem value="PLUS">Plus</MenuItem>
              <MenuItem value="PRO">Pro</MenuItem>
            </Select>
            {errors.category && (
              <Box component="span" sx={{ color: "red", fontSize: "0.75rem" }}>
                {errors.category}
              </Box>
            )}
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={formData.credit_rollover}
                onChange={handleChange}
                name="credit_rollover"
              />
            }
            label="Credit Rollover"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_unlimited}
                onChange={handleChange}
                name="is_unlimited"
              />
            }
            label="Is Unlimited"
          />
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <span style={{ marginRight: 8, fontWeight: "bold" }}>
                Features
              </span>
              <IconButton onClick={addFeature} color="primary">
                <AddIcon />
              </IconButton>
            </Box>
            {formData.features.map((feature, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}
              >
                <label style={{ minWidth: 80 }}>Feature</label>
                <TextField
                  value={feature.feature}
                  onChange={(e) =>
                    updateFeature(index, "feature", e.target.value)
                  }
                  variant="outlined"
                  fullWidth
                  error={Boolean(errors[`features[${index}].feature`])}
                  helperText={errors[`features[${index}].feature`]}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={feature.is_included}
                      onChange={(e) =>
                        updateFeature(index, "is_included", e.target.checked)
                      }
                    />
                  }
                  label="Included"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={feature.is_unlimited_usage}
                      onChange={(e) =>
                        updateFeature(
                          index,
                          "is_unlimited_usage",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label="Unlimited Usage"
                />
                <IconButton onClick={() => removeFeature(index)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Create Plan
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePlan;
